import { useCallback, useEffect, useState } from 'react';
import { message, Modal, Upload, UploadProps } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { UploadOutlined } from '@ant-design/icons';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import { useAuthorization } from '../Authorization';
import { useLatest, useToggle } from 'ahooks';

type ShopeeMultiUploadProps = {
  storeId: string;
  maxCount?: number;
  value?: UploadFile[];
  onChange?: (UploadFile: string[]) => void;
  returnSn: string;
};

/**
 * 支持 shopee 图片上传
 * 后期可能支持视频
 * @param props
 * @constructor
 */
function ShopeeMultiUpload(props: ShopeeMultiUploadProps) {
  const { value, onChange, maxCount = 3, storeId, returnSn } = props;
  const onChangeRef = useLatest(onChange);
  const { t } = useTranslate();
  const { token } = useAuthorization();
  const customUpload = useCustomUpload();
  const [fileList, setFileList] = useState<UploadFile[]>(value || []);
  const [previewModalVisible, { setLeft: closePreview, setRight: openPreview }] = useToggle();
  const [previewFile, setPreviewFile] = useState<{ type: string; url: string }>();

  const customRequest = useCallback(
    (options: Parameters<Required<UploadProps>['customRequest']>[0]) => {
      customUpload(options, {
        storeId,
        fileType: 'IMAGE',
        reqSource: 'SubmitEvidence',
        returnSn,
      });
    },
    [customUpload, storeId, returnSn],
  );

  const beforeUpload = useCallback(
    async (file: File) => {
      if (file.type.startsWith('image')) {
        const isTypeOk = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
        if (!isTypeOk) {
          message.error(t('phrase.最多上传3张图片，支持jpg，png，jpeg格式'));
        }
        const isSizeOk = file.size / 1024 / 1024 <= 10;
        if (!isSizeOk) {
          message.error(t('trade.图片大小', { num: 10 }));
        }
        return isTypeOk && isSizeOk;
      }
      return false;
    },
    [t],
  );

  const handleChange = useCallback(
    (info: UploadChangeParam<any>) => {
      const { fileList } = info;
      setFileList(fileList);
      const fList: UploadFile[] = [];
      fileList.forEach((v) => {
        if (v.error) {
          message.error(`${t('phrase.上传失败')}: ${v.name}`);
        }
        if (v.error || !v.status) {
          return;
        }
        let { url } = v;
        if (v.response?.code === 200) {
          url = v.response?.data?.url;
        }
        fList.push({
          uid: v.uid,
          name: v.name,
          status: v.status,
          url,
        });
      });
      if (fList.length !== fileList.length) {
        setFileList(fList);
      }
    },
    [t],
  );

  useEffect(() => {
    fileList.length && onChangeRef.current?.(fileList.map((v) => v?.response?.data?.url as string));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileList]);

  const handlePreview = useCallback(
    (file: any) => {
      if (file.response) {
        setPreviewFile({
          type: file.type,
          url: file.response.data.url,
        });
        openPreview();
      } else {
        message.error(t('upload.previewFailed'));
      }
    },
    [openPreview, t],
  );

  return (
    <UploadContainer>
      <Upload
        listType="picture-card"
        headers={{
          [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
        }}
        beforeUpload={beforeUpload}
        fileList={fileList}
        onChange={handleChange}
        maxCount={maxCount}
        onPreview={handlePreview}
        accept=".jpg,.jpeg,.png"
        iconRender={(file) => <img alt="" src={file.url} />}
        customRequest={customRequest}
      >
        {fileList.length >= maxCount ? null : (
          <div>
            <UploadOutlined />
            <div>
              {fileList.length} / {maxCount}
            </div>
          </div>
        )}
      </Upload>
      <Tip>{t('ViewRefund.evidenceSpecification')}</Tip>
      <Modal visible={previewModalVisible} footer={null} width={800} onCancel={closePreview}>
        <img alt="example" style={{ width: '100%' }} src={previewFile?.url} />
      </Modal>
    </UploadContainer>
  );
}

const UploadContainer = styled.div``;
const Tip = styled.div`
  color: #84868c;
  font-size: 12px;
`;

export default ShopeeMultiUpload;
